// Creating a survey (redesign spec §8): the survey page's sections, every one
// open, one Create at the bottom. Two forms behind one save bar — the survey
// details and the step list — because the step editors are typed on the step
// list's own form. A new survey starts inactive; it's switched on from its
// page once it's ready.

import { Button, Confirm, DropdownMenu, SectionNav, toast } from "@pej/kore/components";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, Plus } from "lucide-react";
import { useRef, useState } from "react";
import { ApiError } from "@/api/error";
import { fieldErrorsSummary } from "@/api/friendly-error";
import { http } from "@/api/http";
import { surveyKeys } from "@/api/keys";
import { useCreateSurvey } from "@/api/surveys";
import { useTemplates } from "@/api/templates";
import type { StepKind, StepRow, Survey } from "@/api/types";
import { toastFormError } from "@/components/error-toast";
import { PageSaveBar } from "@/components/section-page/page-save-bar";
import { SectionCard } from "@/components/section-page/section-card";
import { SectionPageShell } from "@/components/section-page/section-page-shell";
import { useUnsavedChangesGuard } from "@/components/section-page/use-unsaved-changes-guard";
import { useAssetClientContext } from "@/features/assets/asset-client-context";
import { type PreviewFollow, PreviewPanel } from "@/features/preview/preview-panel";
import { Form, firstFieldMessage, useFieldArray, useForm, useKoreForm } from "@/lib/forms";
import { DEFAULT_LANGUAGE } from "./languages";
import { KIND_LABELS } from "./step-kinds";
import { resolveStepAssets } from "./steps/step-assets";
import { StepFields } from "./steps/step-fields";
import { describeStepsProblem, moveStep, starterSteps, stepPrompt } from "./steps/step-list";
import { useStepsRules } from "./steps/use-steps-rules";
import {
    AppearanceFields,
    GeneralFields,
    HeaderFields,
    surveyDetailsSchema,
    ThankYouFields,
} from "./survey-details-fields";
import { newStep, type StepsFormValues, toDraftSteps, toPutSteps, toStepConfig, validateSteps } from "./survey-schema";
import { SECTION } from "./survey-sections";
import { SURVEY_TEMPLATES, type SurveyTemplateKey } from "./survey-templates";

const START_SECTION = "section-start";
const CREATE_STEP_PREFIX = "section-create-step-";
const createStepSection = (index: number) => `${CREATE_STEP_PREFIX}${index}`;

const ADDABLE_KINDS: StepKind[] = ["choice", "multi", "text"];

// Placements created with the survey: a guest's own device runs inline, a
// shared one is a kiosk that resets between guests, and a mail is a link
// placement answered through invites (email spec §2.2).
const RUNS_ON = [
    {
        key: "own",
        label: "Guests' own devices",
        description: "A QR code, a shared link, or embedded in a web page or app.",
        placement: { name: "Guest devices", mode: "inline" },
    },
    {
        key: "shared",
        label: "A shared device",
        description: "A tablet or kiosk at the venue that resets between guests.",
        placement: { name: "Kiosk", mode: "kiosk" },
    },
    {
        key: "email",
        label: "Sent in an email",
        description: "One link per guest, in an order confirmation or a mailing your system sends.",
        placement: { name: "Email", mode: "link" },
    },
] as const;
type RunsOn = (typeof RUNS_ON)[number]["key"];

export function CreateSurveyPage({ workspaceId }: { workspaceId: string }) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const assetCtx = useAssetClientContext();
    const createSurvey = useCreateSurvey(workspaceId);
    const templatesQuery = useTemplates(workspaceId);
    const [start, setStart] = useState<SurveyTemplateKey>("feedback");
    const [runsOn, setRunsOn] = useState<ReadonlySet<RunsOn>>(() => new Set(["own"]));
    const [pendingStart, setPendingStart] = useState<SurveyTemplateKey | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [moveNotice, setMoveNotice] = useState<string | null>(null);
    const [follow, setFollow] = useState<PreviewFollow | null>({ target: 0, seq: 0 });
    // The whole of create(), not just the POST: checking the fields and
    // uploading step artwork come first, and a second Create in that window
    // would make a second survey. The ref stops the re-entrant call (state
    // only lands on the next render); the state drives the bar.
    const [creating, setCreating] = useState(false);
    const creatingRef = useRef(false);
    const detailsForm = useKoreForm({
        schema: surveyDetailsSchema,
        defaultValues: {
            name: "",
            language: DEFAULT_LANGUAGE,
            active: false,
            templateId: "",
            thankYouText: "",
            displayTitle: "",
            displayDescription: "",
        },
    });
    const stepsForm = useForm<StepsFormValues>({ defaultValues: { steps: starterSteps("feedback") } });
    const stepsArray = useFieldArray({ control: stepsForm.control, name: "steps", keyName: "fieldKey" });
    const rules = useStepsRules(stepsForm);
    const details = detailsForm.watch();
    const stepsValues = stepsForm.watch();
    // Read isDirty during render, each form on its own line: react-hook-form
    // only computes it for a form whose formState was read in render (a
    // short-circuited `a || b` would leave the second unread). The guard
    // reads a ref, which create() clears just before its own navigation.
    const { isDirty: detailsDirty } = detailsForm.formState;
    const { isDirty: stepsDirty } = stepsForm.formState;
    const dirtyRef = useRef(false);
    dirtyRef.current = detailsDirty || stepsDirty;
    const { dialog } = useUnsavedChangesGuard(() => dirtyRef.current);

    function chooseStart(next: SurveyTemplateKey) {
        if (next === start) return;
        if (stepsDirty) {
            setPendingStart(next);
            return;
        }
        applyStart(next);
    }
    function applyStart(next: SurveyTemplateKey) {
        setStart(next);
        setMoveNotice(null);
        stepsForm.reset({ steps: starterSteps(next) });
    }

    function move(index: number, direction: "up" | "down") {
        setMoveNotice(null);
        const result = moveStep(stepsForm.getValues("steps"), index, direction);
        if (!result.ok) {
            setMoveNotice(result.message);
            return;
        }
        stepsArray.replace(result.steps);
    }

    async function create() {
        if (creatingRef.current) return;
        creatingRef.current = true;
        setCreating(true);
        try {
            await createOnce();
        } finally {
            creatingRef.current = false;
            setCreating(false);
        }
    }

    async function createOnce() {
        setError(null);
        const detailsOk = await detailsForm.trigger();
        if (!detailsOk) {
            setError(firstFieldMessage(detailsForm.formState.errors));
            return;
        }
        const values = detailsForm.getValues();
        let steps = stepsForm.getValues("steps");
        if (steps.length > 0) {
            const resolved = await resolveStepAssets(steps, [], assetCtx, queryClient);
            if (!resolved.ok) {
                setError(resolved.message);
                return;
            }
            steps = resolved.steps;
            const problem = validateSteps({ steps });
            if (problem) {
                setError(describeStepsProblem(problem));
                return;
            }
        }
        let created: Survey;
        try {
            created = await createSurvey.mutateAsync({
                name: values.name,
                language: values.language,
                ...(values.templateId ? { templateId: values.templateId } : {}),
                ...(values.thankYouText.trim() ? { thankYouText: values.thankYouText.trim() } : {}),
                ...(values.displayTitle.trim() ? { displayTitle: values.displayTitle.trim() } : {}),
                ...(values.displayDescription.trim() ? { displayDescription: values.displayDescription.trim() } : {}),
                placements: RUNS_ON.filter((option) => runsOn.has(option.key)).map((option) => option.placement),
            });
        } catch (err) {
            if (err instanceof ApiError) {
                // Field violations land on their fields, summed up in the bar;
                // the toast says the rest.
                if (toastFormError("Create failed", detailsForm, err)) setError(fieldErrorsSummary(err) ?? null);
                return;
            }
            throw err;
        }
        let stepsSaved = true;
        if (steps.length > 0) {
            try {
                await http.put<StepRow[]>(`/v1/surveys/${created.id}/steps`, {
                    json: { steps: toPutSteps({ steps }) },
                });
                queryClient.invalidateQueries({ queryKey: surveyKeys.detail(created.id) });
            } catch {
                stepsSaved = false;
                toast.error("Survey created, but its steps could not be saved — add them on the survey page.");
            }
        }
        // Clean before navigating, so the guard lets the navigation through:
        // no render runs between here and navigate(), so clear the ref the
        // guard reads by hand.
        detailsForm.reset(values);
        stepsForm.reset({ steps });
        dirtyRef.current = false;
        // Only when the steps landed (or there were none) — otherwise this
        // would contradict the error toast just shown above.
        if (stepsSaved) toast.success("Survey created");
        navigate({ to: "/w/$workspaceId/surveys/$surveyId", params: { workspaceId, surveyId: created.id } });
    }

    const stepTitle = (index: number) => {
        const value = stepsValues.steps[index];
        return `${index + 1} · ${value ? stepPrompt({ config: toStepConfig(value) }) : "Untitled"}`;
    };
    const templates = templatesQuery.data ?? [];

    return (
        <SectionPageShell
            backLabel="Surveys"
            backLink={<Link to="/w/$workspaceId/surveys" params={{ workspaceId }} />}
            title="New survey"
            nav={
                <SectionNav
                    sticky={false}
                    items={[
                        { id: START_SECTION, label: "Start from" },
                        { id: SECTION.general, label: "General" },
                        { id: SECTION.appearance, label: "Appearance" },
                        { id: SECTION.header, label: "Header" },
                        {
                            id: SECTION.steps,
                            label: "Steps",
                            children: stepsArray.fields.map((_field, index) => ({
                                id: createStepSection(index),
                                label: stepTitle(index),
                            })),
                        },
                        { id: SECTION.thanks, label: "Thank you" },
                        { id: SECTION.placements, label: "Where it runs" },
                    ]}
                />
            }
            preview={
                <PreviewPanel
                    surveyName={details.name || "New survey"}
                    thankYouText={details.thankYouText.trim() || null}
                    displayTitle={details.displayTitle.trim() || null}
                    displayDescription={details.displayDescription.trim() || null}
                    language={details.language}
                    steps={toDraftSteps(stepsValues)}
                    template={templates.find((candidate) => candidate.id === details.templateId) ?? null}
                    follow={follow}
                />
            }
            onNavClick={(id) => {
                const target: PreviewFollow["target"] = id.startsWith(CREATE_STEP_PREFIX)
                    ? Number(id.slice(CREATE_STEP_PREFIX.length))
                    : id === SECTION.thanks
                      ? "thanks"
                      : 0;
                setFollow((previous) => ({ target, seq: (previous?.seq ?? 0) + 1 }));
            }}
        >
            <SectionCard id={START_SECTION} title="Start from">
                <fieldset className="grid gap-3 sm:grid-cols-3">
                    <legend className="sr-only">Start from</legend>
                    {SURVEY_TEMPLATES.map((template) => (
                        <label
                            key={template.key}
                            className="flex cursor-pointer flex-col gap-1 rounded-kore-md border border-kore-border p-3 has-[:checked]:border-kore-accent has-[:checked]:bg-kore-accent-softer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-kore-accent"
                        >
                            <input
                                type="radio"
                                name="start"
                                value={template.key}
                                checked={start === template.key}
                                onChange={() => chooseStart(template.key)}
                                className="sr-only"
                            />
                            <span className="text-sm font-medium">{template.label}</span>
                            <span className="text-xs text-kore-text-muted">{template.description}</span>
                        </label>
                    ))}
                </fieldset>
            </SectionCard>

            <Form form={detailsForm}>
                <div className="flex flex-col gap-6">
                    <SectionCard id={SECTION.general} title="General">
                        <GeneralFields showStatus={false} />
                    </SectionCard>
                    <SectionCard id={SECTION.appearance} title="Appearance">
                        <AppearanceFields workspaceId={workspaceId} />
                    </SectionCard>
                    <SectionCard id={SECTION.header} title="Header">
                        <HeaderFields />
                    </SectionCard>
                </div>
            </Form>

            <section
                id={SECTION.steps}
                aria-labelledby={`${SECTION.steps}-title`}
                className="flex scroll-mt-[4.5rem] flex-col gap-6"
            >
                <div>
                    <h2 id={`${SECTION.steps}-title`} className="text-lg font-semibold">
                        Steps
                    </h2>
                    <p className="mt-1 text-sm text-kore-text-muted">What guests are asked, in order.</p>
                </div>
                {moveNotice && (
                    <p role="alert" className="text-sm text-kore-danger-text">
                        {moveNotice}
                    </p>
                )}
                {stepsArray.fields.map((field, index) => (
                    <SectionCard
                        key={field.fieldKey}
                        id={createStepSection(index)}
                        title={stepTitle(index)}
                        actions={
                            <div className="flex items-center gap-1">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Move step ${index + 1} up`}
                                    disabled={index === 0}
                                    onClick={() => move(index, "up")}
                                >
                                    <ChevronUp aria-hidden="true" />
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Move step ${index + 1} down`}
                                    disabled={index >= stepsArray.fields.length - 1}
                                    onClick={() => move(index, "down")}
                                >
                                    <ChevronDown aria-hidden="true" />
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setMoveNotice(null);
                                        stepsArray.remove(index);
                                    }}
                                >
                                    Remove
                                </Button>
                            </div>
                        }
                    >
                        <Form form={stepsForm}>
                            <StepFields form={stepsForm} index={index} rules={rules} />
                        </Form>
                    </SectionCard>
                ))}
                <div>
                    <DropdownMenu>
                        <DropdownMenu.Trigger render={<Button type="button" variant="outline" size="sm" />}>
                            <Plus aria-hidden="true" />
                            Add step
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content align="start">
                            {ADDABLE_KINDS.map((kind) => (
                                <DropdownMenu.Item key={kind} onClick={() => stepsArray.append(newStep(kind))}>
                                    {KIND_LABELS[kind]}
                                </DropdownMenu.Item>
                            ))}
                        </DropdownMenu.Content>
                    </DropdownMenu>
                </div>
            </section>

            <Form form={detailsForm}>
                <SectionCard id={SECTION.thanks} title="Thank you">
                    <ThankYouFields />
                </SectionCard>
            </Form>

            <SectionCard id={SECTION.placements} title="Where it runs">
                <p className="mb-3 text-sm text-kore-text-muted">
                    Each choice becomes a placement with its own link. You can add or change placements later.
                </p>
                <fieldset className="grid gap-3 sm:grid-cols-3">
                    <legend className="sr-only">Where it runs</legend>
                    {RUNS_ON.map((option) => (
                        <label
                            key={option.key}
                            className="flex cursor-pointer flex-col gap-1 rounded-kore-md border border-kore-border p-3 has-[:checked]:border-kore-accent has-[:checked]:bg-kore-accent-softer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-kore-accent"
                        >
                            <input
                                type="checkbox"
                                name="runsOn"
                                value={option.key}
                                checked={runsOn.has(option.key)}
                                onChange={(event) => {
                                    const checked = event.target.checked;
                                    setRunsOn((previous) => {
                                        const next = new Set(previous);
                                        if (checked) next.add(option.key);
                                        else next.delete(option.key);
                                        return next;
                                    });
                                }}
                                className="sr-only"
                            />
                            <span className="text-sm font-medium">{option.label}</span>
                            <span className="text-xs text-kore-text-muted">{option.description}</span>
                        </label>
                    ))}
                </fieldset>
            </SectionCard>

            {error && (
                <p role="alert" className="text-sm text-kore-danger-text">
                    {error}
                </p>
            )}
            <PageSaveBar
                dirty={detailsDirty || stepsDirty}
                saving={creating}
                saveLabel="Create survey"
                onSave={() => void create()}
                onCancel={() => navigate({ to: "/w/$workspaceId/surveys", params: { workspaceId } })}
            />
            <Confirm
                open={pendingStart !== null}
                onOpenChange={(open) => {
                    if (!open) setPendingStart(null);
                }}
                title="Replace your steps?"
                description="Your edits to the steps will be lost."
                primaryLabel="Replace"
                cancelLabel="Keep editing"
                onConfirm={() => {
                    if (pendingStart) applyStart(pendingStart);
                    setPendingStart(null);
                }}
            />
            {dialog}
        </SectionPageShell>
    );
}
